import { defineStore } from "pinia";

import { JmApi } from "../jm-api/jm-api";
import {
  PLZ_SURCHARGE_TRANSPORTS_URL,
  PLZ_SURCHARGE_URL,
} from "../config/urls";

/** Eine gespeicherte Regel: Aufschlag je PLZ und Verkehrsmittel. */
export interface PlzSurcharge {
  id?: number;
  office_id?: number;
  plz: string;
  transport_type: string;
  surcharge_pct: number;
  surcharge_minutes: number;
}

/** Ein zulaessiges Verkehrsmittel, wie das Backend es meldet. */
export interface PlzSurchargeTransport {
  transport_type: string;
  column_prefix: string;
  road_bound: boolean;
}

interface State {
  surcharges: PlzSurcharge[];
  transports: PlzSurchargeTransport[];
  currentOfficeId: number | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /**
   * Ob die Ergebnisse mit Aufschlag angezeigt werden.
   *
   * Bewusst hier und nicht in der Komponente: Karte und Ergebnistabellen
   * muessen denselben Zustand sehen, sonst zeigen zwei Ansichten desselben
   * Standorts unterschiedliche Zahlen.
   */
  enabled: boolean;
}

function messageOf(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return error instanceof Error ? error.message : "Unbekannter Fehler";
}

export const usePlzSurchargeStore = defineStore("plzSurcharge", {
  state: (): State => ({
    surcharges: [],
    transports: [],
    currentOfficeId: null,
    loading: false,
    saving: false,
    error: null,
    enabled: false,
  }),

  getters: {
    /** Alle Regeln, nach PLZ gruppiert. */
    byPlz(state): Record<string, PlzSurcharge[]> {
      return state.surcharges.reduce(
        (acc, rule) => {
          (acc[rule.plz] ||= []).push(rule);
          return acc;
        },
        {} as Record<string, PlzSurcharge[]>,
      );
    },

    /** Postleitzahlen, fuer die ueberhaupt ein Aufschlag gepflegt ist. */
    configuredPlz(state): string[] {
      return [...new Set(state.surcharges.map((rule) => rule.plz))].sort();
    },

    /**
     * Hoechster Aufschlag je PLZ - Grundlage fuer die Einfaerbung der Karte.
     * Prozent und Minuten sind nicht vergleichbar, deshalb getrennt.
     */
    maxByPlz(state): Record<string, { pct: number; minutes: number }> {
      const result: Record<string, { pct: number; minutes: number }> = {};
      for (const rule of state.surcharges) {
        const current = (result[rule.plz] ||= { pct: 0, minutes: 0 });
        current.pct = Math.max(current.pct, Number(rule.surcharge_pct) || 0);
        current.minutes = Math.max(
          current.minutes,
          Number(rule.surcharge_minutes) || 0,
        );
      }
      return result;
    },

    /** Straßengebundene Verkehrsmittel - Vorbelegung im Eingabedialog. */
    roadBoundTransports(state): string[] {
      return state.transports
        .filter((transport) => transport.road_bound)
        .map((transport) => transport.transport_type);
    },

    /** Ob fuer den aktuellen Standort ueberhaupt Aufschlaege existieren. */
    hasSurcharges(state): boolean {
      return state.surcharges.length > 0;
    },
  },

  actions: {
    /** Regeln eines Standorts laden. */
    async fetchSurcharges(officeId: number): Promise<PlzSurcharge[]> {
      if (!officeId) {
        this.error = "Kein Standort ausgewählt";
        return [];
      }
      this.loading = true;
      this.error = null;
      this.currentOfficeId = officeId;
      try {
        const response = await JmApi.get(`${PLZ_SURCHARGE_URL}/${officeId}`);
        this.surcharges = (response ?? []) as unknown as PlzSurcharge[];
        return this.surcharges;
      } catch (error) {
        this.error = messageOf(error);
        this.surcharges = [];
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /** Zulaessige Verkehrsmittel laden; aendert sich nicht, deshalb nur einmal. */
    async fetchTransports(): Promise<PlzSurchargeTransport[]> {
      if (this.transports.length > 0) return this.transports;
      try {
        const response = await JmApi.get(PLZ_SURCHARGE_TRANSPORTS_URL);
        this.transports = (response ??
          []) as unknown as PlzSurchargeTransport[];
        return this.transports;
      } catch (error) {
        this.error = messageOf(error);
        throw error;
      }
    },

    /**
     * Regeln speichern (Upsert).
     *
     * Ein Aufschlag von 0 in beiden Feldern loescht die Regel - das macht das
     * Backend so, und es ist der Weg, ueber den die Oberflaeche einen
     * Aufschlag wieder entfernt.
     */
    async saveSurcharges(
      officeId: number,
      entries: Partial<PlzSurcharge>[],
    ): Promise<PlzSurcharge[]> {
      this.saving = true;
      this.error = null;
      try {
        const response = await JmApi.put(
          `${PLZ_SURCHARGE_URL}/${officeId}`,
          entries as unknown as object,
        );
        this.surcharges = (response ?? []) as unknown as PlzSurcharge[];
        this.currentOfficeId = officeId;
        return this.surcharges;
      } catch (error) {
        this.error = messageOf(error);
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /**
     * Denselben Aufschlag auf eine ganze PLZ-Auswahl anwenden.
     *
     * Das ist der Weg des Karten-Dialogs: erst mehrere Gebiete anklicken oder
     * mit einem Polygon einrahmen, dann einmal den Wert vergeben. Ein Request
     * statt n mal 17. Achtung: bestehende Werte der Auswahl werden ersetzt,
     * nicht gemischt.
     */
    async applyToPlzList(
      officeId: number,
      plzList: string[],
      values: {
        transport_type: string;
        surcharge_pct?: number;
        surcharge_minutes?: number;
      }[],
    ): Promise<PlzSurcharge[]> {
      this.saving = true;
      this.error = null;
      try {
        const response = await JmApi.post(
          `${PLZ_SURCHARGE_URL}/${officeId}/apply`,
          { plz_list: plzList, values },
        );
        this.surcharges = (response ?? []) as unknown as PlzSurcharge[];
        this.currentOfficeId = officeId;
        return this.surcharges;
      } catch (error) {
        this.error = messageOf(error);
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /** Regeln einer PLZ loeschen, wahlweise nur fuer ein Verkehrsmittel. */
    async deleteSurcharge(
      officeId: number,
      plz: string,
      transportType?: string,
    ): Promise<void> {
      this.saving = true;
      this.error = null;
      try {
        const query = transportType
          ? `?transportType=${encodeURIComponent(transportType)}`
          : "";
        await JmApi.delete(
          `${PLZ_SURCHARGE_URL}/${officeId}/plz/${encodeURIComponent(plz)}${query}`,
        );
        await this.fetchSurcharges(officeId);
      } catch (error) {
        this.error = messageOf(error);
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /** Anzeige mit oder ohne Aufschlag. */
    setEnabled(value: boolean) {
      this.enabled = value;
    },

    /** Beim Verlassen einer Ansicht aufraeumen. */
    reset() {
      this.surcharges = [];
      this.currentOfficeId = null;
      this.error = null;
      this.enabled = false;
    },
  },
});
